import { mxCell } from 'mxgraph';
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import "./GlyphEditor.css";
import { AppContext } from '../App';
import { GlobalState, Glyph, GlyphChange, GlyphState, IdeaBox, IdeaBoxEntry, Project, TaskType } from 'konect-model';
import UserControlService, { UserControlEvent } from '../../services/user-control-service';
import { nanoid } from 'nanoid';
import Backlog from './Backlog';
import { DragDropContext, Droppable, Position } from 'react-beautiful-dnd';
import { DropType } from './GlyphGrouping';
import { GlyphEvent, GlyphEventService } from '../../services/event-service';
import { MDBSwitch, MDBTooltip } from 'mdb-react-ui-kit';
import { cloneDeep } from 'lodash';
import GlyphEditorState from './GlyphEditorState';
import { InfoWindowState } from './InfoWindow';
import { Settings } from '../Settings';
import { MXGraphService } from '../../services/mxgraph-service';
import InterpolationMenu from './InterpolationMenu';
var mx = require("mxgraph")({
    mxImageBasePath: "mxgraph/javascript/src/images",
    mxBasePath: "mxgraph/javascript/src"
})

interface GlyphEditorProps {
    initialGlyph: Glyph;
    compositionMode?: boolean;
}

export const EDITOR_URL = window.location.origin + "/www/index.html";
export const DEFAULT_XML = '<mxGraphModel><root><mxCell id="0"/><mxCell id="1" parent="0"/></root></mxGraphModel>';

const callbackID = { undoId: nanoid(), redoId: nanoid(), saveId: nanoid() };

const renderEntries : GlyphState[] = [];

export default function GlyphEditor(props: GlyphEditorProps) {

    const { project, setShowInfo } = useContext(AppContext);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [glyph, setGlyph] = useState<Glyph>(props.initialGlyph || new Glyph());
    const [isDragging, setIsDragging] = useState<boolean | string>(false);
    const iframe = useRef<any>();
    const svg = useRef<any>();
    const editor = useRef<any>();
    const graph = useRef<any>();
    const animationAnchor = useRef<Position>({ x: 0, y: 0 });
    const glyphRef = useRef<Glyph>(props.initialGlyph || glyph);
    const [glyphState, setGlyphState] = useState<GlyphState>(glyphRef.current.getDefaultState());
    const [activeGlyphStates, setActiveGlyphStates] = useState<GlyphState[]>([]);
    const activeGlyphStatesRef = useRef<GlyphState[]>([]);
    const glyphStateRef = useRef<GlyphState>(glyphState);
    const [globalState, setGlobalState] = useState<GlobalState>(project.globalStates && project.globalStates.length > 0 ? project.globalStates[0] : new GlobalState(nanoid(), nanoid()));
    const globalStateRef = useRef<GlobalState>(globalState);
    const hideGlyphs = useRef(Settings.sideNav.hideGlyphs);
    const annotationModeRef = useRef(false);
    const [annotationMode, setAnnotationMode] = useState(false);

    useEffect(() => {
        UserControlService.disable(true);
        initMessageHandler();

        // once glyph editor is closed reenable/redo
        return () => {
            GlyphEventService.reset();
            UserControlService.enable();
        }
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    useLayoutEffect(() => {
        GlyphEventService.reset();
        loadEditor(glyph);
        initMessageHandler();

        // THIS RETURN FUNCTION IS CALLED WITH PREVIOUS VALUES BEFORE THE NEW USELAYOUTEFFECT IS EXECUTED, THIS IS USED TO KEEP THE STATE UPON LEAVING THE PAGE
        return () => {
            if (!graph.current || !editor.current) {
                return;
            }

            project.graphXML = editor.current.createXML(graph.current.model);
            //@ts-ignore
            if (svg.current) glyph.svg = svg.current.children[1].children[0].children[1].cloneNode(true);

        }

    }, [])  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { // disable glyph editor in compose mode
        graph.current && graph.current.setEnabled(!props.compositionMode);
    }, [props.compositionMode])

    useEffect(() => {
        activeGlyphStatesRef.current = activeGlyphStates;
    }, [activeGlyphStates])

    // Effect for handling the changes when the user selects a different glyph
    useEffect(() => {
        glyphRef.current = glyph;

        if (!graph.current) {
            return
        }
        glyphStateRef.current = glyphRef.current.getDefaultState();
        setGlyphState(glyphStateRef.current);
        graph.current.setDefaultParent(glyphRef.current.model);
        setActiveGlyphStates([]);
        for (const g of project.glyphs) {
            let cell = g.model;
            if (!cell) continue;
            MXGraphService.disableCell(cell);
        }
        MXGraphService.enableCell(glyphRef.current.model);
        graph.current.orderCells(false, [glyph.model]);
        graph.current.setSelectionCells([]);
        graph.current.isNotInDefaultState = false;
        refreshGraph();
        //graph.current.view.setCurrentRoot(glyph.model);
    }, [glyph])  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setGlyph(props.initialGlyph);
    }, [props.initialGlyph])

    // listens to the iframe to load the editor
    const initMessageHandler = () => {
        window.onmessage = function (e) {
            e.data === "loaded" && initEditor()
        }
    }

    const initEditor = () => {
        // init editor only once
        if (loaded) { return; }

        loadEditor(glyph);
        setLoaded(true);

        const frame: any = iframe.current;
        const model = graph.current.model;

        // SAVE/REDO/UNDO
        editor.current.onSave = () => {
            UserControlService.save();
        }

        editor.current.undoManager.onUndoAvailable = (notAvailable?: boolean) => {
            !notAvailable && UserControlService.dispatch(UserControlEvent.SAVE_AVAILABLE, true);
            UserControlService.dispatch(UserControlEvent.UNDO_AVAILABLE, !notAvailable);
        }

        editor.current.undoManager.onRedoAvailable = (notAvailable?: boolean) => {
            !notAvailable && UserControlService.dispatch(UserControlEvent.SAVE_AVAILABLE, true);
            UserControlService.dispatch(UserControlEvent.REDO_AVAILABLE, !notAvailable);
        }

        UserControlService.on(UserControlEvent.UNDO, () => {
            editor.current.undoManager.undo();
        }, callbackID.undoId);

        UserControlService.on(UserControlEvent.REDO, () => {
            editor.current.undoManager.redo();
        }, callbackID.redoId);

        UserControlService.on(UserControlEvent.BEFORE_SAVING, () => {
            project.graphXML = editor.current.createXML(graph.current.model);
        }, callbackID.saveId)

        // HANDLE EVENTS FROM OTHER COMPONENT
        GlyphEventService.on(GlyphEvent.REMOVE_LINKS, (entry: IdeaBoxEntry) => {
            entry.linkedCells = [];
            graph.current.setSelectionCells([]);
            graph.current.removeAllLabels();
            graph.current && renderGlyphStates([...activeGlyphStatesRef.current])
        })
        GlyphEventService.on(GlyphEvent.TOGGLE_HIDE_GLYPHS, (hide: boolean) => {
            hideGlyphs.current = hide;
            graph.current && renderGlyphStates([...activeGlyphStatesRef.current])
        })

        // HANDLE GRAPH EVENTS
        graph.current.addListener(mx.mxEvent.CELLS_REMOVED, (sender: any, evt: any) => {
            removeCellLinks(evt.properties.cells);
            removeCellsFromState(glyphStateRef.current, evt.properties.cells);
            setIsDragging(!isDragging); //ugly hack to rerender the backlogs 
            setIsDragging(isDragging);
        });

        graph.current.addListener(mx.mxEvent.CELLS_ADDED, (sender: any, evt: any) => {
            copyCellLinks(evt.properties.cells);
            addCellsToState(glyphStateRef.current, evt.properties.cells);
        });

        model.addListener(mx.mxEvent.CHANGE, function (sender: any, evt: any) {
            var changes = evt.getProperty('edit').changes;
            onCellChange(changes);
        });

        graph.current.onGroupingCanceled = () => {
            UserControlService.showInfoPopup(
                {
                    title: `Action Disabled`,
                    content: `Grouping and ungrouping is only enabled in default states.`,
                    duration: 5000,
                    color: "danger"
                });
        }

        // prevents cells from being deleted when they are deleted within a non-default state and hides them instead
        graph.current.beforeCellRemoval = (cells: mxCell[]) => {
            if (glyphStateRef.current.id === "default") {
                // return true with this function to cancel cell removal in mxGraph;
                return false;
            }
            const states = glyphRef.current.states;
            for (const cell of cells) {
                hideCellInState(cell, glyphStateRef.current);
                let changeEntry = glyphStateRef.current.changes.find(c => c.cellId === cell.id);
                if (changeEntry) {
                    changeEntry.geometry = undefined;
                    changeEntry.style = { hide: "1" };
                    changeEntry.value = undefined;
                }
                if (MXGraphService.cellIsVisibleInNoState(cell.id, states)) {
                    graph.current.removeCells([cell]);
                }
            }
            editor.current.undoManager.clear();
            renderGlyphStates(activeGlyphStatesRef.current);
            return true;
        }

        graph.current.onRemoveLinkFromCell = (cell: mxCell, entry: IdeaBoxEntry) => {
            removeEntryLink(cell, entry);
        };

        // if a ideaboxentry label is dragged and dropped within the editor reposition it based on its target cell
        graph.current.handleLabelDrop = (event: any, origin: mxCell) => {
            const x = event.clientX + (svg.current.scrollLeft - svg.current.offsetLeft);
            const y = event.clientY + (svg.current.scrollTop - svg.current.offsetTop);
            const cell = graph.current.getCellAt(x, y);
            if (!cell) return;
            const entries = project.getIdeaboxEntriesOfGlyph(glyph);
            const entry = entries.find((e: IdeaBoxEntry) => e.id === event.entry.id);
            if (!entry) return;
            graph.current.model.beginUpdate();
            try {
                removeEntryLink(origin, event.entry);
                MXGraphService.linkCellToIdeaBoxEntry(cell, entry);
            } finally {
                graph.current.model.endUpdate();
            }
            refreshGraph();
            graph.current.setSelectionCells([cell]);
        }

        // load the used color history
        const colorDialog = frame.contentWindow.ColorDialog;
        colorDialog.recentColors = project.editorColors ? [...project.editorColors] : []
        // save the color history when colors are added to the color palette
        colorDialog.onRecentColorAdded = (colors: string[]) => {
            project.editorColors = [...colors];
        }

        if (props.compositionMode) {
            renderGlobalState(project.globalStates[0])
            return
        }
        renderGlyphStates([]);
    }

    const refreshGraph = () => {
        for (const glyph of project.glyphs) {
            let cell = glyph.model;
            if (props.compositionMode) {
                cell.setVisible(true);
                MXGraphService.enableCell(cell);
            }
            else {
                cell.setVisible(!hideGlyphs.current)
            }
        }

        glyphRef.current && glyphRef.current.model && glyphRef.current.model.setVisible(true);
        graph.current && graph.current.refresh && graph.current.refresh();
        if (hideGlyphs.current) {
            return;
        }
        for (const glyph of project.glyphs) {
            let cell = glyph.model;
            if (!cell) continue;
            graph.current.setCellOpacity(cell, props.compositionMode ? 1 : 0.25);
        }
        graph.current.setCellOpacity(glyphRef.current.model, 1);
    }

    const loadEditor = (initialGlyph: Glyph) => {
        const frame: any = iframe.current;
        glyphRef.current = initialGlyph;
        if (!frame || !frame.contentWindow.editor || !frame.contentWindow.graph) {
            return;
        }

        graph.current = frame.contentWindow.graph;
        MXGraphService.addIdeaBoxEntryLabelSupport(graph.current, frame.contentWindow, project.ideaBox);

        editor.current = frame.contentWindow.editor;
        svg.current = frame.contentWindow.document.getElementsByClassName("geDiagramContainer")[0];

        editor.current.loadXML(project.graphXML ? project.graphXML : DEFAULT_XML);

        //remove any cells that are not included in the default state
        const defaultState = project.getDefaultGlyphState();
        const allCells = MXGraphService.getAllChildrenOfCell(graph.current.model.root);
        allCells.shift();
        for (const cell of allCells) {
            if (!defaultState.changes.find(change => change.cellId === cell.id) && !project.glyphs.find(g => g.id === cell.id)) {
                graph.current.removeCells([cell]);
            }
        }

        for (const g of project.glyphs) {
            let cell = graph.current.model.getCell(g.id)
            if (!cell) cell = graph.current.addCell(new mx.mxCell(g.name), graph.current.model.root);
            cell.setId(g.id);
            g.model = cell;
            if (cell.id !== initialGlyph.id) {
                MXGraphService.disableCell(cell);
            } else {
                MXGraphService.enableCell(cell);
            }
        }

        fixOldGlyphs();

        setGlyphState(glyphRef.current.getDefaultState());
        graph.current.orderCells(false, [initialGlyph.model]);
        graph.current.setDefaultParent(initialGlyph.model);

        refreshGraph();
    }

    // currently removes all links instead of copying them
    const copyCellLinks = (cells: mxCell[]) => {
        //const glyphEntries = Glyph.getAllGlyphEntries(glyphRef.current);
        for (const cell of cells) {
            cell.setAttribute('entryIDs', "[]");
            cell.setAttribute('entry-labels', "");
            cell.children && copyCellLinks(cell.children);
            /** USE THIS INSTEAD OF THE PREVIOUS TTHREE LINES IF YOU WANT TO ALSO COPY LINKS FOR COPIED CELLS INSTEAD OF REMOVING THEM
            const entryIDs = JSON.parse(cell.getAttribute("entryIDs", "[]")) || [];
            for (const id of entryIDs) {
                const entry = glyphEntries.find((e: IdeaBoxEntry) => e.id === id);
                if (entry) {
                    entry.linkedCells.push(cell.id);
                }
            }
                 */
        }
    }

    const removeCellLinks = (cells: mxCell[]) => {
        for (const entry of project.ideaBox.entries) {
            for (const cell of cells) {
                MXGraphService.unlinkCellFromIdeaboxEntry(cell, entry);
                if (cell.children && cell.children.length > 0) {
                    removeCellLinks(cell.children);
                }
            }
        }
    }

    const onDragStart = (result: any) => {
        setIsDragging(result.draggableId);
    }

    const onDragEnd = (e: any) => {
        setIsDragging(false);
        graph.current.highlightCellsWithEntry([], true);
        const entries = project.ideaBox.entries;
        const entry = entries.find((e: IdeaBoxEntry) => e.id === isDragging);
        if (!entry) return
        const selection = graph.current.getSelectionCells();
        // Add cell at mouse position to selection if one or no cell selected
        if (selection.length <= 1) {
            const cell = getCellAtMouseEvent(e);
            cell && (selection[0] = cell);
        }
        selection.length > 0 && setCellAnimationAnchor(selection[0], e);
        graph.current.model.beginUpdate();
        try {
            selection.forEach((cell: any) => {
                MXGraphService.linkCellToIdeaBoxEntry(cell, entry);
            });
        } finally {
            graph.current.model.endUpdate();
        }
        refreshGraph();
        graph.current.setSelectionCells(selection);
    }

    const getCellAtMouseEvent = (e: any) => {
        const bounds = e.target.getBoundingClientRect();
        const pos = { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
        pos.x += svg.current.scrollLeft - svg.current.offsetLeft;
        pos.y += svg.current.scrollTop - svg.current.offsetTop;
        return graph.current.getCellAt(pos.x, pos.y);
    }

    const setCellAnimationAnchor = (cell?: mxCell, e?: MouseEvent) => {
        if (!cell || !e) {
            animationAnchor.current = { x: 0, y: 0 }
            return false;
        }
        const state = graph.current.view.getState(cell);
        animationAnchor.current.x = state.x + state.width / 2 - svg.current.scrollLeft + svg.current.offsetLeft - iframe.current.offsetLeft;
        animationAnchor.current.y = state.y + state.height / 2 - svg.current.scrollTop + svg.current.offsetTop - iframe.current.offsetTop;
        return animationAnchor.current;
    }

    const backlogHovered = (entry: IdeaBoxEntry, isHovered: boolean) => {
        loaded && graph.current.highlightCellsWithEntry(entry, isHovered);
    }

    const removeEntryLink = (cell: mxCell, entry: IdeaBoxEntry) => {
        MXGraphService.unlinkCellFromIdeaboxEntry(cell, entry);
        graph.current.setSelectionCells([]);
        setIsDragging(!isDragging); //ugly hack to rerender the backlogs
        setIsDragging(isDragging);
    }

    const getDragStyle = (isDragging: boolean | string, isDraggingOver: boolean) => {
        const style: any = { pointerEvents: "all", filter: "", zIndex: "1" }
        if (isDragging) {
            style.zIndex = "-1";
            style.pointerEvents = "none";
            svg.current && (svg.current.childNodes[0].style.filter = "brightness(0.9)");
        } else {
            svg.current && (svg.current.childNodes[0].style.filter = "");
        }
        if (isDraggingOver) {
            //style.filter = "brightness(0.9)";
        }
        return style;
    }

    const renderGlyphMenuBar = useMemo(() => {
        if (props.compositionMode) {
            return <></>
        }
        return <div className='glyph-menu-bar mb-3' style={{ whiteSpace: "nowrap", overflowX: "auto", marginBottom: "1rem" }}>
            <span style={{ borderColor: "transparent" }}>
                Glyphs:
            </span>
            {project.glyphs.map((gly: Glyph, index) =>
                <span key={'gmb' + index}
                    className={glyph.id === gly.id ? "active" : ""}
                    onClick={() => { editor.current.undoManager.clear(); setGlyph(gly); }}>
                    {gly.name ? gly.name : "Unnamed Glyph"}
                </span>
            )}
            {/**
            <div className="sidenav-setting d-flex justify-content-between align-center ms-3" style={{ alignItems: "center" }}>
                <MDBSwitch onChange={(e: any) => { toggleGlyphVisiblity(e); }} defaultChecked />
                <span style={{ fontWeight: "bold" }} className='text-dark'>Hide Glyphs</span>
            </div>
              */}
            <span className={"download-button"}
                onClick={() => { setShowInfo(InfoWindowState.GLYPH_SKETCHING) }}>
                <i className="fas fa-question" style={{ margin: "0 -1px" }}></i>
            </span>
        </div>
    }, [project, glyph, props.compositionMode, glyphState]);  // eslint-disable-line react-hooks/exhaustive-deps

    ///// Functions for managing glyph states
    const addCellsToState = (state: GlyphState, cells: mxCell[]) => {
        const defaultState = glyphRef.current.getDefaultState();

        for (const cell of cells) {
            let changeEntry = new GlyphChange(cell.id, annotationModeRef.current);
            changeEntry.style = MXGraphService.styleStringToObject(cell.style);
            changeEntry.geometry = { ...cell.getGeometry() };
            const value = cell.getValue();
            if (value) {
                changeEntry.value = value.getAttribute ? value.getAttribute("label") : value;
            }

            //either add the new change or replace it if it already exists
            let index = defaultState.changes.findIndex(c => c.cellId === cell.id);
            if (index >= 0) {
                defaultState.changes.splice(index, 1, changeEntry);
            } else {
                defaultState.changes.push(changeEntry)
            }

            // hide the cell in the default state if added to a state that is not the default state
            if (state.id !== "default") {
                hideCellInState(cell, defaultState);
            }

            // also add children to the state if there are any
            cell.children && cell.children.length > 0 && addCellsToState(defaultState, cell.children);
        }
    }

    const removeCellsFromState = (state: GlyphState, cells: mxCell[]) => {
        for (const g of project.glyphs) {
            for (const state of g.states) {
                state.changes = state.changes.filter(c => !cells.find(cell => cell && cell.id === c.cellId));
            }
        }
    }

    const onCellChange = (changes: any[]) => {
        for (const change of changes) {
            if (change.parent || change.child) {
                //handle parent changes
                //return;
            }
            if (!change.cell) continue
            let changeEntry = glyphStateRef.current.changes.find(c => c.cellId === change.cell.id);
            if (!changeEntry) {
                changeEntry = new GlyphChange(change.cell.id, annotationModeRef.current);
                glyphStateRef.current.changes.push(changeEntry);
            }
            if (change.style) {
                changeEntry.style = Object.assign(changeEntry.style, MXGraphService.getStyleDifference(change.previous, change.style));
            }
            if (change.geometry) {
                let defaultEntry = glyphRef.current.getDefaultState().changes.find(c => c.cellId === change.cell.id);
                if (glyphStateRef.current.id === "default") { //default state always holds the full geometry
                    changeEntry.geometry = Object.assign(changeEntry.geometry, change.geometry);
                } else if (defaultEntry && defaultEntry.geometry) { //every other state holds the difference that has to be added to the default geometry while rendering
                    changeEntry.geometry = Object.assign(changeEntry.geometry, MXGraphService.getGeometryDifference(defaultEntry.geometry, change.geometry));
                }
            }
            if ((change.value || change.value === "")) {
                //if value is a node, get the label attribute, otherwise just the plain value
                changeEntry.value = change.value.getAttribute ? change.value.getAttribute("label") : change.value;
            }
            changeEntry.target = change.cell.target ? change.cell.target.id : undefined;
            changeEntry.source = change.cell.source ? change.cell.source.id : undefined;

        }
    }

    const renderGlyphStates = (states: GlyphState[]) => {
        if (!graph.current) {
            return;
        }
        const baseState = project.getDefaultGlyphState();
        graph.current.getModel().beginUpdate();
        try {
            for (const change of baseState.changes) {
                const cell = graph.current.getModel().getCell(change.cellId);
                if (!cell) {
                    continue;
                }
                cell.setStyle(MXGraphService.styleObjectToString(MXGraphService.getCellStyleForGlyphStates(cell, baseState, states)));
                cell.setGeometry(MXGraphService.getCellGeometryForGlyphStates(cell, baseState, states));
                MXGraphService.setVariableCellLabel(cell, MXGraphService.getCellLabelForGlyphStates(cell, baseState, states));
                MXGraphService.setCellTerminals(cell, baseState, states, graph.current.getModel());
            }
        }
        finally {
            graph.current.getModel().endUpdate();
            refreshGraph();
            graph.current.setSelectionCells([]);
        }
    }

    const addGlyphState = () => {
        const newState = new GlyphState(nanoid(), "");
        glyphRef.current.states.push(newState);
        editGlyphState(newState);
    }

    const editGlyphState = (state: GlyphState, isDefault?: boolean) => {
        graph.current.isNotInDefaultState = !isDefault;
        editor.current.undoManager.clear();
        glyphStateRef.current = state;
        setActiveGlyphStates(isDefault ? [] : [state]);
        setGlyphState(state);
        renderGlyphStates(isDefault ? [] : [state]);
    }

    const toggleActiveState = (state: GlyphState) => {
        const i = activeGlyphStates.findIndex(s => s.id === state.id);
        if (i >= 0 && state.id === glyphStateRef.current.id) {
            setGlyphState(glyphRef.current.getDefaultState())
        }
        if (i >= 0) {
            activeGlyphStates.splice(i, 1);
        } else {
            activeGlyphStates.push(state);
        }
        setActiveGlyphStates([...activeGlyphStates]);
        renderGlyphStates([...activeGlyphStates])
    }

    const editDefaultState = () => {
        editGlyphState(glyphRef.current.getDefaultState(), true);
    }

    const copyGlyphState = (state: GlyphState) => {
        const copy = cloneDeep(state);
        copy.id = nanoid();
        copy.name = state.name + "_copy";
        glyphRef.current.states.push(copy);
        editGlyphState(copy);
    }

    const deleteGlyphState = (state: GlyphState) => {
        const i = glyphRef.current.states.findIndex(s => s.id === state.id);
        if (i < 0) return
        glyphRef.current.states.splice(i, 1);
        const states = glyphRef.current.states;
        for (const change of state.changes) {
            if (MXGraphService.cellIsVisibleInNoState(change.cellId, states)) {
                const cell = graph.current.getModel().getCell(change.cellId);
                graph.current.removeCells([cell]);
            }
        }
        editDefaultState();
    }

    const hideCellInState = (cell: mxCell, state: GlyphState, dontHide?: boolean) => {
        let changeEntry = state.changes.find(c => c.cellId === cell.id);
        if (!changeEntry) {
            changeEntry = new GlyphChange(cell.id, annotationModeRef.current);
            changeEntry.style.hide = dontHide ? "0" : "1";
            state.changes.push(changeEntry);
            return;
        }
        if (!changeEntry.style) {
            changeEntry.style = {};
        }
        changeEntry.style.hide = dontHide ? "0" : "1";
    }

    const hideCellsInState = (cells: mxCell[], state: GlyphState, dontHide?: boolean) => {
        for (const cell of cells) {
            hideCellInState(cell, state, dontHide);
        }
    }

    const renderStateMenuBar = useMemo(() => {
        if (props.compositionMode || !glyph.states) {
            return <></>
        }
        return <div className='glyph-menu-bar mb-3'>
            <span style={{ borderColor: "transparent" }}>
                View States:
            </span>
            {glyph.states.map((sta: GlyphState, index) =>
                <GlyphEditorState
                    key={index}
                    state={sta}
                    isEdit={glyphState.id === sta.id}
                    isDefaultState={sta.id === "default"}
                    isActive={!!activeGlyphStates.find(s => s.id === sta.id)}
                    onCopy={copyGlyphState}
                    onDefaultEdit={editDefaultState}
                    onDelete={deleteGlyphState}
                    onEdit={editGlyphState}
                    onMouseEnter={() => { index > 0 ? renderGlyphStates(glyphState.id === sta.id ? [...activeGlyphStates] : [...activeGlyphStates, sta]) : renderGlyphStates([]) }}
                    onMouseLeave={() => { renderGlyphStates([...activeGlyphStates]) }}
                    onToggleActive={() => { toggleActiveState(sta) }}
                />
            )}
            <span className={"active"}
                onClick={() => { addGlyphState() }}>
                +
            </span>
        </div>
    }, [project, glyph, props.compositionMode, glyphState, activeGlyphStates]);  // eslint-disable-line react-hooks/exhaustive-deps

    ////////////////////////////////////
    // Functions for managing global states

    const addGlobalState = () => {
        const newState = new GlobalState(nanoid(), "");
        project.globalStates.push(newState)
        setGlobalState(newState);
        renderGlobalState(newState);
    }

    const editGlobalState = (state: GlobalState) => {
        const actualState = state ? state : project.globalStates[0];
        setGlobalState(actualState);
        renderGlobalState(actualState);
    }

    const deleteGlobalState = (state: GlobalState) => {
        const i = project.globalStates.findIndex(s => s.id === state.id);
        if (i < 0) return
        project.globalStates.splice(i, 1);
        setGlobalState(project.globalStates[0]);
        renderGlobalState(project.globalStates[0]);
    }

    const renderGlobalState = (globalState: GlobalState, add?: GlyphState) => {
        if (!graph.current) {
            return;
        }
        let states = project.getGlyphStatesOfGlobalState(globalState);
        if (add) {
            states.push(add)
        }

        renderGlyphStates(states);
    }

    const toggleGlobalState = (globalState: GlobalState, state: GlyphState) => {
        const i = globalState.activeGlyphStateIds.findIndex(id => id === state.id);
        if (i < 0) {
            globalState.activeGlyphStateIds.push(state.id)
        } else {
            globalState.activeGlyphStateIds.splice(i, 1);
        }
        setGlobalState({ ...globalState });
    }

    const renderGlobalStateBar = useMemo(() => {
        if (!project.globalStates) {
            return <></>
        }
        let allStates: GlyphState[] = [];
        for (const glyph of project.glyphs) {
            allStates = [...allStates, ...glyph.states.filter(s => s.id !== "default")]
        }

        return <><div className='glyph-menu-bar global-states-bar-top' style={{ whiteSpace: "nowrap", overflowX: "auto", marginBottom: "1rem" }}>
            <span style={{ borderColor: "transparent" }}>
                Global States:
            </span>
            {project.globalStates.map((sta: GlobalState, index) =>
                <GlyphEditorState
                    key={index}
                    state={sta}
                    isEdit={globalState.id === sta.id}
                    isDefaultState={index <= 0}
                    isActive={false}
                    onDefaultEdit={editGlobalState}
                    onDelete={deleteGlobalState}
                    onEdit={editGlobalState}
                    onMouseEnter={() => { renderGlobalState(sta) }}
                    onMouseLeave={() => { renderGlobalState(globalState) }}
                />
            )}
            <span className={"active"}
                onClick={() => { addGlobalState() }}>
                +
            </span>
            <span className={"download-button"}
                onClick={() => { MXGraphService.exportGraph(graph.current) }}>
                PNG <i className="fas fa-download" />
            </span>
        </div>
            <div style={{ whiteSpace: "nowrap", overflowX: "auto", marginBottom: "1rem" }}>
                {project.glyphs.map((g, index) => g.states.length > 0 ? <div key={"t" + index} className='glyph-menu-bar global-states-bar pt-1'>{g.states.map((sta: GlyphState, index) => {
                    if (sta.id === "default") return undefined
                    return <GlyphEditorState
                        key={index}
                        state={sta}
                        isEdit={false}
                        onEdit={() => { toggleGlobalState(globalState, sta) }}
                        isActive={!!globalState.activeGlyphStateIds.find(id => id === sta.id)}
                        onMouseEnter={() => { renderGlobalState(globalState, sta) }}
                        onMouseLeave={() => { renderGlobalState(globalState) }}
                        onToggleActive={() => { toggleGlobalState(globalState, sta) }}
                    />
                }
                )}<div className='state-title'>{g.name}</div></div> : null)}
            </div>
        </>
    }, [project, glyph, props.compositionMode, glyphState, activeGlyphStates, globalState]);  // eslint-disable-line react-hooks/exhaustive-deps


    ////////////////////////////////////
    // Updates old projects to support the state system
    const fixOldGlyphs = () => {
        //@ts-ignore Support glyphs that had no states prior
        if (!project.defaultState) {
            for (const glyph of project.glyphs) {
                if (glyph.states || !glyph.model) {
                    continue;
                }
                glyph.states = [new GlyphState("default", "default")];
                const cells = MXGraphService.getAllChildrenOfCell(glyph.model);
                MXGraphService.enableCell(glyph.model);
                if (cells.length > 1) {
                    cells.shift(); //remove first child cell because that is the glyph container
                    //@ts-ignore
                    addCellsToState(glyph.getDefaultState(), cells);
                }
            }
            return
        }

        //@ts-ignore // Support glyphs that were dependend on the globa default state in project.defaultState
        if (!project.defaultState || project.defaultState.changes.length <= 0) return
        for (const glyph of project.glyphs) {
            if (!glyph.model) continue
            if (!glyph.states) {
                glyph.states = [new GlyphState("default", "default")];
            }
            if (!glyph.getDefaultState()) {
                glyph.states.unshift(new GlyphState("default", "default"));
            }

            const cells = MXGraphService.getAllChildrenOfCell(glyph.model);
            MXGraphService.enableCell(glyph.model);
            const defaultState = glyph.getDefaultState();
            for (const cell of cells) {
                //@ts-ignore
                const change = project.defaultState.changes.find(c => c.cellId === cell.id);
                if (!change) continue
                const index = defaultState.changes.findIndex(c => c.cellId === change.id)
                if (index >= 0) {
                    defaultState.changes[index] = change;
                } else {
                    defaultState.changes.push(change);
                }
            }
        }

        //@ts-ignore
        delete project["defaultState"];
    }
    /////////////////////////////////

    const updateInterpolation = (state: GlyphState) => {
        const index = renderEntries.findIndex(e => e.id === state.id);
        if (index >= 0) {
            renderEntries[index] = state;
        } else {
            renderEntries.push(state);
        }
        renderGlyphStates(renderEntries);
        graph.current && graph.current.removeAllLabels();
    }

    //////

    const entries = project.getIdeaboxEntriesOfGlyph(glyph);

    return <div id="glyph-editor">
        {!props.compositionMode && renderGlyphMenuBar}
        {!props.compositionMode ? renderStateMenuBar : renderGlobalStateBar}
        <DragDropContext onDragEnd={() => { setIsDragging(false) }} onBeforeCapture={onDragStart}>
            <div className='d-flex flex-grow-1' style={{ height: "0%" }}>
                <Droppable droppableId="glyph-editor" direction="vertical" type={DropType.TASK}>
                    {(provided, snapshot) => (
                        <div {...provided.droppableProps}
                            ref={provided.innerRef}
                            className='glyph-editor-frame-container' style={{ flex: 1, ...(isDragging ? { cursor: "crosshair" } : {}) }}>
                            {!loaded && <i className={"fas fa-spinner glyph-editor-spinner"} />}
                            <div onMouseLeave={() => { animationAnchor.current = { x: 0, y: 0 }; }}
                                onMouseUp={onDragEnd}
                            >
                                <iframe id={"glyph-editor-frame"}
                                    className='glyph-editor-frame' title="glyph-editor-frame"
                                    ref={iframe}
                                    style={getDragStyle(isDragging, snapshot.isDraggingOver)}
                                    src={EDITOR_URL} />
                                {!props.compositionMode &&
                                    <div className="annotate-button sidenav-setting d-flex justify-content-between align-center ms-3" style={{ alignItems: "center", backgroundColor: annotationMode ? "#f93154" : "var(--mdb-gray)" }}>
                                        <MDBSwitch onChange={(e: any) => { annotationModeRef.current = !annotationModeRef.current; setAnnotationMode(!annotationMode) }} defaultChecked={annotationModeRef.current} />
                                        <span style={{ fontWeight: "bold" }} className='text-white'>Annotation Mode</span>
                                        <MDBTooltip tag='span' wrapperClass='d-inline-block' title='Use annotation mode if you want to add elements or descriptions to your design that are not an actual part of the design. All elements that are added to your design while the annotation mode is turned on will be ignored during the KONECT-Value calculation.'>
                                            <i className="fas fa-question card-exclamation"></i></MDBTooltip>
                                    </div>}
                            </div>
                            <div style={{ display: "none" }}>{provided.placeholder}</div>
                        </div>
                    )}
                </Droppable>

                {!props.compositionMode ?
                    <div className='backlog-container'>
                        <Backlog entries={entries}
                            type={TaskType.INFORMATION}
                            name="Perception"
                            animateToRef={animationAnchor}
                            onHover={backlogHovered} />
                        <Backlog entries={entries}
                            type={TaskType.INTERACTION}
                            name="Interaction"
                            animateToRef={animationAnchor}
                            onHover={backlogHovered} />
                    </div>
                    :
                    <InterpolationMenu onChange={updateInterpolation}/>
                }

            </div>
        </DragDropContext>

    </div>
}